import { type CSSProperties } from "react";
import { ChevronLeft, ChevronRight, Home, LayoutGrid, Sparkles, User } from "lucide-react";
import "./BottomNav.css";

/**
 * Bottom navigation, mobile only. ONE floating glass island, always present:
 * a slim round-status row (round, hook line, prev/next) over the tab row,
 * divided by a hairline, the way a mini player sits over a tab bar. No modes,
 * no popping, no detached buttons.
 */

export type NavTab = "feed" | "stories" | "insights" | "you";
/** Legacy type from the removed three-mode bar dance. App.tsx still passes
 *  `mode` (and mirrors it onto <html data-bar>), but this component is static
 *  now and never reads it. Kept only so the App.tsx call site type-checks. */
export type BarMode = "hidden" | "status" | "menu";

const TABS: { id: NavTab; label: string; Icon: typeof Home }[] = [
  { id: "feed", label: "Feed", Icon: Home },
  { id: "stories", label: "Stories", Icon: LayoutGrid },
  { id: "insights", label: "Insights", Icon: Sparkles },
  { id: "you", label: "You", Icon: User },
];

interface Props {
  mode: BarMode; // legacy-static: accepted from App.tsx, intentionally unused

  active: NavTab;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onSelect: (tab: NavTab) => void;
  onPrev?: () => void;
  onNext?: () => void;
  roundLabel?: string;
  roundNote?: string;
  hook?: string;
  streak?: number;
  savedCount?: number;
  freshCount?: number;
  signedIn?: boolean;
  accountName?: string;
}

function initialsOf(name?: string): string {
  if (!name) return "";
  const clean = name.split("@")[0].replace(/[^A-Za-z ]/g, " ").trim();
  if (!clean) return "";
  const parts = clean.split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

export default function BottomNav({
  active, onSelect, onPrev, onNext, roundLabel, roundNote, hook,
  streak = 0, freshCount = 0, signedIn = false, accountName,
}: Props) {
  const activeIndex = Math.max(0, TABS.findIndex((t) => t.id === active));
  const initials = signedIn ? initialsOf(accountName) : "";
  const streakFrac = streak > 0 ? Math.min(1, (streak % 7 === 0 ? 7 : streak % 7) / 7) : 0;

  return (
    <nav className="bottom-nav" data-tour="nav-btn" aria-label="Primary" style={{ ["--bn-active"]: activeIndex } as CSSProperties}>
      <div className="bn-bar" role="tablist">
        {roundLabel || hook ? (
          <div className="bn-status">
            <button type="button" className="bn-step" onClick={onPrev} aria-label="Previous story">
              <ChevronLeft size={17} aria-hidden="true" />
            </button>
            <div className="bn-status-copy">
              <span className="bn-round">
                {roundLabel}
                {roundNote ? <em>{roundNote}</em> : null}
              </span>
              {hook ? <span className="bn-hook">{hook}</span> : null}
            </div>
            <button type="button" className="bn-step" onClick={onNext} aria-label="Next story">
              <ChevronRight size={17} aria-hidden="true" />
            </button>
          </div>
        ) : null}
        <div className="bn-tabs">
          <span className="bn-indicator" aria-hidden="true" />
          {TABS.map((t, i) => {
            const isActive = t.id === active;
            const Icon = t.Icon;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={t.label}
                className={`bn-tab${isActive ? " active" : ""}`}
                style={{ ["--i"]: i } as CSSProperties}
                onClick={() => onSelect(t.id)}
              >
                <span className="bn-ico">
                  {t.id === "you" ? (
                    <span className={`bn-avatar${streak > 0 ? " ring" : ""}`} style={{ ["--bn-streak"]: streakFrac } as CSSProperties}>
                      <span className="bn-avatar-inner">
                        {initials ? <span className="bn-initials">{initials}</span> : <Icon size={16} aria-hidden="true" />}
                      </span>
                    </span>
                  ) : (
                    <Icon size={22} aria-hidden="true" />
                  )}
                  {t.id === "feed" && freshCount > 0 ? <span className="bn-dot" aria-hidden="true" /> : null}
                </span>
                <span className="bn-label">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
